import { autoinject, transient, BindingEngine, useView } from "aurelia-framework";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { DataGridColumn, DataType, SourceType } from "../../../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import { MenuItem } from "../../../../../components/menu/menu-item";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { RightshipConfirmationDialogModel } from "../../../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { BudgetClientService } from "../../../../../../services/budget/budget-client-service";
import { BusinessUnitModel } from "../../../../../../models/customers/customer.models";
import { BudgetClientModel } from "../../../../../../models/rightstore/rightstore.models";
import * as moment from "moment";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";

@autoinject
@transient()
@useView('views/pages/modules/business-unit/tabs/rightstore-budget/business-unit-rightstore-budget.html')
export class BusinessUnitRightstoreBudget extends CustomGridPageBase {

    constructor(protected i18n: I18N, protected bindingEngine: BindingEngine, private dialogClientService: DialogClientService,
        private budgetClientService:BudgetClientService, public notificationService: NotificationClientService) {
        super(i18n, "Rightstore Budget", bindingEngine)
    }

    businessUnitId:number;
    totalBudget: number;
    totalAvailableCredit: number;

    activate(model: BusinessUnitModel) {
        this.businessUnitId = model.businessUnitId;
        this.reload();
    }

    async reload() {
        const budgets = await this.budgetClientService.getAll(this.businessUnitId);

        const totalPaidCredits = budgets.map(x => x.paidCredits).reduce((acc, val) => acc + val, 0);
        const totalBonusCredits = budgets.map(x => x.bonusCredits).reduce((acc, val) => acc + val, 0);
        const totalExpiredCredits = budgets.map(x => x.expiredCredits).reduce((acc, val) => acc + val, 0);
        const totalUsedCredits = budgets.map(x => x.usedCredits).reduce((acc, val) => acc + val, 0);

        this.totalBudget = (totalPaidCredits + totalBonusCredits) - totalExpiredCredits
        this.totalAvailableCredit = this.totalBudget - totalUsedCredits

        this.itemSource = {
            columns: this.getColumns(),
            source: budgets,
            sourceType: SourceType.Collection,
        }
    }

    getColumns(): DataGridColumn[] {
        return [
            {
                sortOrder: 2,
                columnOrder: 2,
                name: "budgetId",
                dataType: DataType.Number,
                title: "Bundle ID"
            },
            {
                sortOrder: 3,
                columnOrder: 3,
                name: "paidCredits",
                dataType: DataType.Number,
                title: "Paid Credits"
            },
            {
                sortOrder: 4,
                columnOrder: 4,
                name: "bonusCredits",
                dataType: DataType.Number,
                title: "Bonus Credits"
            },
            {
                sortOrder: 5,
                columnOrder: 5,
                name: "activationDate",
                dataType: DataType.Date,
                title: "Activation Date",
                valueConverter: (x) => moment(x).format("Do MMMM YYYY")
            },
            {
                sortOrder: 6,
                columnOrder: 6,
                name: "expirationDate",
                dataType: DataType.Date,
                title: "Expiration Date",
                valueConverter: (x) => moment(x).format("Do MMMM YYYY")
            },
            {
                sortOrder: 7,
                columnOrder: 7,
                name: "usedCredits",
                dataType: DataType.Number,
                title: "Used Credits"
            },
            {
                sortOrder: 8,
                columnOrder: 8,
                name: "expiredCredits",
                dataType: DataType.Number,
                title: "Expired Credits"
            },
            {
                columnOrder: 99,
                sortOrder: 99,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" show.bind="isVisible">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    isVisible: true,
                    menuItems: (x) => 
                    {
                        const menuItems: MenuItem[] = [];
                
                        menuItems.push({isVisible: true, click: () => this.edit(x), item: { text: "Edit" }, classList: ''});
                        menuItems.push({isVisible: true, click: () => this.delete(x), item: { text: "Delete" }, classList: ''});
                        return menuItems;
                    }
                }
            }
        ];

    }

    edit(x:BudgetClientModel) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddRightstoreBundleDialog, { businessUnitId:this.businessUnitId, budgetId: x.budgetId, reload:this.reload.bind(this) }, true, false, false, 'right');
    }

    add() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddRightstoreBundleDialog, { businessUnitId:this.businessUnitId, budgetId: 0, reload:this.reload.bind(this) }, true, false, false, 'right');
    }

    delete(item:BudgetClientModel) {
        const model = new RightshipConfirmationDialogModel();
        if (item.usedCredits > 0) {
            this.notificationService.show("Cannot delete budget when used credit greater than 0", "", NotificationType.Error, 5000);
            return;
        }

        model.confirm = this.i18N.tr('delete-text');
        model.cancel = this.i18N.tr('cancel-text');

        model.messages.push(this.i18N.tr('delete-entity-description', {
            name: `Bundle ID ${item.budgetId}`
        }));

        model.title = this.i18N.tr('delete-entity-text', {
            name: `Bundle ID ${item.budgetId}`
        });
        
        model.onConfirm = (async () => {
            await this.budgetClientService.delete(item.budgetId);
            this.reload();
        }).bind(this);

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
    }
}